/**
 * Error response structure based on RFC 9457
 * As defined in the BOL API OpenAPI specification
 */
interface ErrorResponse {
  title?: string;
  status?: number;
  code?: string;
  detail?: string;
  instance?: string;
}

/**
 * Extracts detailed error information from API error responses
 * Supports both the RFC 9457 ErrorResponse format and legacy formats
 * Handles ResponseError from generated API clients
 * 
 * @param err - The error object caught from an API call
 * @param fallbackMessage - Default message if no specific error details are found
 * @returns A formatted error message with all available details
 */
export async function extractErrorMessage(err: unknown, fallbackMessage: string): Promise<string> {
  // Handle AbortError (request was cancelled)
  if (err instanceof Error && err.name === 'AbortError') {
    return 'Request was cancelled';
  }

  // Handle ResponseError from generated API clients (has Response object)
  if (err && typeof err === 'object' && 'response' in err) {
    const errorWithResponse = err as { response?: Response | { data?: unknown } };
    
    // If response is a fetch Response object, try to read the body
    if (errorWithResponse.response && typeof errorWithResponse.response === 'object') {
      const response = errorWithResponse.response;
      
      // Check if it's a fetch Response with json() method
      if ('json' in response && typeof response.json === 'function') {
        try {
          // Clone the response to avoid consuming the body
          const clonedResponse = (response as Response).clone();
          const errorData = await clonedResponse.json() as ErrorResponse & { message?: string; error?: string };
          
          const message = buildErrorMessageFromData(errorData);
          if (message) return message;
        } catch {
          // If JSON parsing fails, fall through to other checks
        }
      }
      
      // Check if response has a data property (axios-style or already parsed)
      if ('data' in response && response.data && typeof response.data === 'object') {
        const errorData = response.data as ErrorResponse & { message?: string; error?: string };
        const message = buildErrorMessageFromData(errorData);
        if (message) return message;
      }
    }
  }

  // Fallback to error message property
  if (err instanceof Error && err.message) {
    // Don't return generic "Response returned an error code" message
    if (err.message !== 'Response returned an error code') {
      return err.message;
    }
  }

  // Last resort fallback
  return fallbackMessage;
}

/**
 * Builds an error message from RFC 9457 error data or legacy formats
 */
function buildErrorMessageFromData(errorData: ErrorResponse & { message?: string; error?: string }): string {
  // Build detailed error message from RFC 9457 format with better formatting
  const lines: string[] = [];
  
  // Main error title and status on first line
  if (errorData.title) {
    let firstLine = errorData.title;
    if (errorData.status) {
      firstLine += ` (Status: ${errorData.status})`;
    }
    lines.push(firstLine);
  } else if (errorData.status) {
    lines.push(`Error (Status: ${errorData.status})`);
  }
  
  // Error code on second line if available
  if (errorData.code) {
    lines.push(`Code: ${errorData.code}`);
  }
  
  // Detailed description on third line if available
  if (errorData.detail) {
    lines.push(`Detail: ${errorData.detail}`);
  }
  
  // If we have detailed error info, return it with line breaks
  if (lines.length > 0) {
    return lines.join('\n');
  }
  
  // Fallback to legacy error formats
  if (errorData.message) {
    return errorData.message;
  }
  
  if (errorData.error) {
    return errorData.error;
  }
  
  return '';
}

/**
 * Formats an error for user-facing display with a prefix
 * 
 * @param err - The error object caught from an API call
 * @param operation - The operation that failed (e.g., "Create quotation request")
 * @param fallbackMessage - Default message if no specific error details are found
 * @returns A formatted error message like "Create quotation request failed: Bad Request [bol.bad_request.field.not_found] Field not found quotationStatuser (Status: 400)"
 */
export async function formatOperationError(err: unknown, operation: string, fallbackMessage: string): Promise<string> {
  const errorMessage = await extractErrorMessage(err, fallbackMessage);
  return `${operation} failed: ${errorMessage}`;
}
